import { ErrorCodes, getMessage } from '@limitless-minds/app-messages'
import { Button } from '@limitless-minds/ui-button'
import { Input } from '@limitless-minds/ui-input'
import { Stack, styled } from '@mui/material'
import React, { useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { useRegister, useValidateEmailInvite } from '@/data/auth'
import { PATHS } from '@/router/constants'
import { useStore } from '@/store'
import { Error as IError } from '@/types/Error'
import ErrorMessage from '@/ui/atoms/ErrorMessage'
import HeaderWrapper from '@/ui/molecules/HeaderWrapper'

const StyledDiv = styled(Stack)(`
  justify-content: center;
  align-items: center;
  align-self: center;
  width: 100%;
  max-width: 25rem;
  height: 100%;
  gap: 2rem;
`)

const StepThreePointFive: React.FC = () => {
  const navigate = useNavigate()
  const { id: qrId } = useParams<{ id: string }>()
  const [textCode, setTextCode] = useState('')
  const [failedRegisterMessage, setFailedRegisterMessage] = useState('')
  const [textCodeError, setTextCodeError] = useState('')
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const store = useStore()
  const { mutate: register } = useRegister()
  const { mutate: validateEmailInvite } = useValidateEmailInvite()
  const { increaseStep } = store

  const registerWithPayload = (extraBody?: { textCode?: string }): void => {
    const { firstName, lastName, displayName, timezone, email, password } = store

    // If qrCodeId exists, don't send textCode
    const body: {
      firstName: string
      lastName: string
      displayName: string
      timezone: string
      email: string
      password: string
      qrCodeId?: string
      textCode?: string
    } = {
      firstName,
      lastName,
      displayName,
      timezone,
      email,
      password,
    }

    if (qrId) {
      body.qrCodeId = qrId
    } else if (extraBody?.textCode) {
      body.textCode = extraBody.textCode
    }

    register(
      {
        body,
      },
      {
        onSuccess: () => {
          setFailedRegisterMessage('')
          setTextCode('')
          setTextCodeError('')
          increaseStep()
        },
        onError: (error) => {
          const errorResponse = error as IError
          const errorCode = errorResponse.response?.data?.error?.errorCode
          if (errorCode === ErrorCodes.SIGNUP.USER_IS_ALREADY_ACTIVE) {
            navigate(PATHS.LOGIN)
          }
          if (errorCode === ErrorCodes.QR.NOT_FOUND) {
            setTextCodeError('Text Code invalid')
            setFailedRegisterMessage('')
            return
          }
          setTextCodeError('')
          const errorMessage = getMessage(errorCode, errorResponse.response?.data?.error?.params)
          setFailedRegisterMessage(errorMessage)
        },
      }
    )
  }

  const handleEnterCode = (): void => {
    registerWithPayload({ textCode })
  }

  const handleNoCode = (): void => {
    // Access store values directly to get the most up-to-date values
    const { firstName, lastName, displayName, timezone, password } = store

    if (token) {
      validateEmailInvite(
        {
          body: {
            firstName,
            lastName,
            displayName,
            timezone,
            password,
            token,
          },
        },
        {
          onSuccess: () => {
            setFailedRegisterMessage('')
            // Clear the local storage of Zustand store
            useStore.persist.clearStorage()
            navigate(PATHS.LOGIN)
          },
          onError: (error) => {
            const errorResponse = error as IError
            const errorMessage = getMessage(
              errorResponse.response?.data?.error?.errorCode,
              errorResponse.response?.data?.error?.params
            )
            setFailedRegisterMessage(errorMessage)
          },
        }
      )
    } else {
      registerWithPayload()
    }
  }

  return (
    <StyledDiv>
      <HeaderWrapper
        title='Join your company on Neumi'
        // eslint-disable-next-line max-len
        description='Enter your company activation code below if you have any. If you dont have a code feel free to skip this step.'
      />
      <Stack gap='2rem' width='100%'>
        {failedRegisterMessage !== '' && <ErrorMessage>{failedRegisterMessage}</ErrorMessage>}
        <Input
          name=''
          label='Activation Code'
          value={textCode}
          onChange={(e) => {
            setTextCode(e.target.value)
            if (textCodeError) {
              setTextCodeError('')
            }
          }}
          placeholder='Enter code'
        />
        <Stack gap='0.75rem' direction='row'>
          <Button sx={{ flex: 1, height: '4rem' }} onClick={handleEnterCode}>
            Enter Code
          </Button>
          <Button sx={{ flex: 1, height: '4rem' }} onClick={handleNoCode} design='secondary'>
            I Don't Have a Code
          </Button>
        </Stack>
        {textCodeError !== '' && <ErrorMessage>{textCodeError}</ErrorMessage>}
      </Stack>
    </StyledDiv>
  )
}

export default StepThreePointFive
